import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import type { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import {
  Chip,
  DataRow,
  DateRangePicker,
  FormField,
  IconButton,
  ScrollArea,
  Select,
} from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Download04Icon,
  PencilEdit02Icon,
  PlusSignIcon,
  File02Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { ADD_REPORT, GET_REPORTS } from "graphql/report";
import {
  DEFAULT_ANALYTIC_TRANSACTION_SETTINGS,
  MY_ANALYTIC_TRANSACTION_SETTINGS,
  REMOVE_USER_SETTINGS,
} from "graphql/user";
import {
  TimeFrameEnum,
  TimeFrameTitles,
  timeFrameRange,
} from "enums/AnalyticsTimeFrame";
import { useDateRangePickerLabels } from "utils/dateRangeFilter";
import CardHeader from "components/common/CardHeader";
import CustomSkeleton from "components/common/CustomSkeleton";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import AnalyticTransactionSettingsDrawer, {
  ReportTemplate,
  ReportTemplateColumn,
} from "components/admin/analyticSettings/AnalyticTransactionSettingsDrawer";

interface Report {
  id: string;
  fileName?: string | null;
  reportUrl?: string | null;
  updatedAt?: string | null;
  status?: "GENERATING" | "ACTIVE" | null;
}

interface DateRange {
  from: Dayjs | null;
  to: Dayjs | null;
}

interface TemplateDrawer {
  template: ReportTemplate | null;
  columns: ReportTemplateColumn[];
}

const EMPTY_RANGE: DateRange = { from: null, to: null };
const REPORT_FORMAT = "YYYY/MM/DD";
const REPORT_LIST_MAX_HEIGHT = 320;
/** How often the list rechecks while a report it shows is still generating. */
const GENERATING_POLL_MS = 10000;

const isGenerating = (report: Report) => report.status === "GENERATING";

const ReportsPanel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const pickerLabels = useDateRangePickerLabels();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);
  const [drawer, setDrawer] = useState<TemplateDrawer | null>(null);

  const {
    data: templateData,
    loading: loadingTemplates,
    refetch: refetchTemplates,
  } = useQuery<{ myAnalyticTransactionSettings: ReportTemplate[] }>(
    MY_ANALYTIC_TRANSACTION_SETTINGS,
  );
  const { data: defaultColumnData, loading: loadingDefaults } = useQuery<{
    defaultAnalyticTransactionSettings: ReportTemplateColumn[];
  }>(DEFAULT_ANALYTIC_TRANSACTION_SETTINGS);
  const {
    data: reportData,
    loading: loadingReports,
    refetch: refetchReports,
    startPolling,
    stopPolling,
  } = useQuery<{ reports: { edges: { node: Report }[] } }>(GET_REPORTS, {
    variables: {},
    fetchPolicy: "network-only",
  });

  // A drawer opened before the defaults arrive would start from an empty column list.
  const defaultsReady = !!defaultColumnData;
  const templates = useMemo(
    () => templateData?.myAnalyticTransactionSettings ?? [],
    [templateData],
  );
  const template = templates.find((item) => item.id === templateId) ?? null;
  const reports = (reportData?.reports?.edges ?? []).map(({ node }) => node);
  const anyGenerating = reports.some(isGenerating);

  useEffect(() => {
    if (!anyGenerating) return;
    startPolling(GENERATING_POLL_MS);
    return stopPolling;
  }, [anyGenerating, startPolling, stopPolling]);

  const [addReport, { loading: addingReport }] = useMutation(ADD_REPORT, {
    onCompleted: () => {
      toast.success(t("generatedReport"));
      setTemplateId(null);
      setRange(EMPTY_RANGE);
      refetchReports();
    },
    onError: (error) => toast.error(error.message),
  });

  const [removeTemplate, { loading: removingTemplate }] = useMutation(
    REMOVE_USER_SETTINGS,
    {
      onCompleted: () => {
        toast.success(t("Settings removed!"));
        refetchTemplates();
        setTemplateId(null);
        setDrawer(null);
      },
      onError: (error) => toast.error(error.message),
    },
  );

  const presets = useMemo(
    () =>
      Object.entries(TimeFrameTitles).map(([id, title]) => ({
        id,
        label: t(title),
        resolve: (now: Dayjs) => timeFrameRange(id as TimeFrameEnum, now),
      })),
    [t],
  );

  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [i18n.language],
  );
  const reportDate = (report: Report) => {
    if (!report.updatedAt) return undefined;
    const raw = /^\d+$/.test(report.updatedAt)
      ? Number(report.updatedAt)
      : report.updatedAt;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? undefined : dateFormat.format(date);
  };

  // A new template starts from every default column; an edit offers the ones it lacks.
  const openDrawer = (edited: ReportTemplate | null) => {
    const held = new Set(edited?.settingData.map((column) => column.name));
    const columns = (
      defaultColumnData?.defaultAnalyticTransactionSettings ?? []
    ).filter((column) => !held.has(column.name));
    setDrawer({ template: edited, columns });
  };

  const download = (report: Report) => {
    if (isGenerating(report)) {
      toast.warn(t("reportIsInProgress"));
      return;
    }
    if (report.reportUrl) {
      window.open(report.reportUrl, "_blank", "noopener,noreferrer");
    }
  };

  const generate = () => {
    if (!templateId || !range.from || !range.to) return;
    addReport({
      variables: {
        input: {
          startDate: range.from.format(REPORT_FORMAT),
          endDate: range.to.format(REPORT_FORMAT),
          templateId,
        },
      },
    });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        padding: "var(--space-6)",
        gap: "var(--space-5)",
      }}
    >
      <CardHeader title={t("Transaction Reports")} />

      <Columns gap="var(--space-7)" count={{ xs: 1, lg: 2 }}>
        <Stack gap="var(--space-5)" sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h4">
            {t("Generate Report")}
          </Typography>

          <Stack gap="var(--space-2)">
            <Inline gap="var(--space-3)" align="end" wrap={false}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Select
                  size="md"
                  label={t("analyticSettings.reportTemplate")}
                  options={templates.map((item) => ({
                    id: item.id,
                    label: item.name,
                  }))}
                  value={templateId}
                  onChange={setTemplateId}
                  clearable
                  loading={loadingTemplates}
                  bottomAction={{
                    label: t("Add New Template"),
                    icon: <HugeiconsIcon icon={PlusSignIcon} size={16} />,
                    onClick: () => openDrawer(null),
                    disabled: !defaultsReady,
                    loading: loadingDefaults,
                  }}
                />
              </Box>
              {template && (
                <IconButton
                  aria-label={t("Edit")}
                  disabled={!defaultsReady}
                  onClick={() => openDrawer(template)}
                >
                  <HugeiconsIcon icon={PencilEdit02Icon} size={18} />
                </IconButton>
              )}
            </Inline>
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("analyticSettings.selectYourReportTemplateWillBeGenerated")}
            </Typography>
          </Stack>

          <FormField
            label={t("Range")}
            helperText={t("Select the date range the report should encompass")}
          >
            <DateRangePicker
              size="medium"
              from={range.from}
              to={range.to}
              onChange={(from, to) => setRange({ from, to })}
              presets={presets}
              labels={pickerLabels}
            />
          </FormField>

          <Inline>
            <Button
              variant="primary-filled"
              loading={addingReport}
              disabled={!templateId || !range.from || !range.to}
              onClick={generate}
            >
              {t("Generate")}
            </Button>
          </Inline>
        </Stack>

        <Stack gap="var(--space-4)" sx={{ minWidth: 0 }}>
          <Stack gap="var(--space-1)">
            <Typography variant="h6" component="h4">
              {t("Download")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t(
                "Reports can be downloaded up to 30 business days from generation",
              )}
            </Typography>
          </Stack>

          {loadingReports && reports.length === 0 ? (
            <CustomSkeleton variant="list" rows={3} hideCircular />
          ) : reports.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("No Reports Available")}
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                maxHeight: REPORT_LIST_MAX_HEIGHT,
              }}
            >
              <ScrollArea
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
                contentSx={{ flex: 1, minHeight: 0, height: "auto" }}
              >
                <Stack gap="var(--space-3)">
                  {reports.map((report) => (
                    <DataRow
                      key={report.id}
                      type="amount"
                      icon={{
                        glyph: <HugeiconsIcon icon={File02Icon} size={18} />,
                        tone: "neutral",
                      }}
                      title={report.fileName ?? ""}
                      subtitle={reportDate(report)}
                      tag={
                        isGenerating(report) ? (
                          <Chip size="sm" tone="info" dot>
                            {t("generating")}
                          </Chip>
                        ) : undefined
                      }
                      trailing={
                        <IconButton
                          variant="ghost"
                          aria-label={`${t("Download")} ${report.fileName ?? ""}`}
                          onClick={() => download(report)}
                        >
                          <HugeiconsIcon icon={Download04Icon} size={18} />
                        </IconButton>
                      }
                    />
                  ))}
                </Stack>
              </ScrollArea>
            </Box>
          )}
        </Stack>
      </Columns>

      <AnalyticTransactionSettingsDrawer
        open={drawer !== null}
        busy={removingTemplate}
        selectedSettingData={drawer?.template ?? null}
        defaultColumns={drawer?.columns ?? []}
        handleCloseDrawer={() => setDrawer(null)}
        handleSuccess={(saved) => {
          refetchTemplates();
          setTemplateId(saved.id);
        }}
        handleRemoveSettings={() => {
          if (!drawer?.template) return;
          removeTemplate({
            variables: {
              userSettingType: "ANALYTICS_TRANSACTION",
              input: { id: drawer.template.id },
            },
          });
        }}
      />
    </Card>
  );
};

export default ReportsPanel;
